import React, { useEffect, useMemo, useState } from 'react';
import { ClockIcon, SearchIcon, UserPlusIcon, UsersIcon, XIcon } from 'lucide-react';
import { useData } from '../../store/DataContext';
import { Card } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Encounter } from '../../types';
const WAITING_STAGES: Encounter['stage'][] = ['NURSING', 'CLINICIAN'];
const stageLabel: Record<string, {
  label: string;
  className: string;
}> = {
  NURSING: {
    label: 'Waiting · Nursing / vitals',
    className: 'bg-amber-100 text-amber-800'
  },
  CLINICIAN: {
    label: 'Waiting · Clinician',
    className: 'bg-blue-100 text-blue-800'
  }
};
export function ReceptionQueue() {
  const {
    encounters,
    patients,
    createEncounter
  } = useData();
  const [search, setSearch] = useState('');
  const [now, setNow] = useState(() => Date.now());
  const [adding, setAdding] = useState(false);
  const [addSearch, setAddSearch] = useState('');
  const [notice, setNotice] = useState('');
  useEffect(() => {
    const timer = window.setInterval(() => setNow(Date.now()), 30000);
    return () => window.clearInterval(timer);
  }, []);

  // Live: encounters are synced in real time, so this list updates whenever a
  // patient is added, moves stage, is attended to, or leaves the queue.
  const queue = useMemo(() => encounters.filter(e => WAITING_STAGES.includes(e.stage)).map(encounter => ({
    encounter,
    patient: patients.find(p => p.id === encounter.patientId)
  })).filter((row): row is {
    encounter: Encounter;
    patient: NonNullable<typeof row.patient>;
  } => Boolean(row.patient)).sort((a, b) => new Date(a.encounter.startedAt).getTime() - new Date(b.encounter.startedAt).getTime()), [encounters, patients]);
  const queuedPatientIds = useMemo(() => new Set(queue.map(row => row.patient.id)), [queue]);
  const visible = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return queue;
    return queue.filter(({
      patient
    }) => `${patient.firstName} ${patient.lastName} ${patient.bmcFileNumber}`.toLowerCase().includes(q));
  }, [queue, search]);
  const addCandidates = useMemo(() => {
    const q = addSearch.trim().toLowerCase();
    if (!q) return [];
    return patients.filter(p => `${p.firstName} ${p.lastName} ${p.bmcFileNumber}`.toLowerCase().includes(q)).slice(0, 8);
  }, [patients, addSearch]);
  const handleAdd = (patientId: string, name: string) => {
    if (queuedPatientIds.has(patientId)) return;
    createEncounter(patientId);
    setNotice(`${name} added to the queue.`);
    setAddSearch('');
    setAdding(false);
  };
  return <Card title="Queue" subtitle={`${queue.length} patient${queue.length === 1 ? '' : 's'} waiting · updates live`} action={<Button size="sm" variant={adding ? 'outline' : 'primary'} onClick={() => {
    setAdding(value => !value);
    setNotice('');
  }} data-id="element-10273" data-mp="2f34hpf2cptly">
          {adding ? <>
              <XIcon className="mr-1.5 h-4 w-4" data-id="element-10274" data-mp="2nwrbizn00msi" /> Close
            </> : <>
              <UserPlusIcon className="mr-1.5 h-4 w-4" data-id="element-10275" data-mp="2st817y9uj97p" /> Add patient to queue
            </>}
        </Button>} data-id="element-10272" data-mp="2bjx9ltvo4mbn">
      {notice && <p role="status" className="mb-4 rounded-md border border-green-200 bg-green-50 px-3 py-2 text-sm text-green-800" data-id="element-10276" data-mp="205lw2lkyzwiq">
          {notice}
        </p>}

      {adding && <div className="mb-5 rounded-lg border border-blue-100 bg-blue-50/50 p-4" data-id="element-10277" data-mp="2a4kvddjuift2">
          <label htmlFor="queue-add-search" className="mb-1 block text-sm font-medium text-gray-700" data-id="element-10278" data-mp="3gk3vd21rygi4">
            Find a registered patient
          </label>
          <input id="queue-add-search" type="search" autoFocus value={addSearch} onChange={e => setAddSearch(e.target.value)} placeholder="Name or BMC number" className="block w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500" data-id="element-10279" data-mp="yara4i5py7dt" />
          {addSearch.trim() && <ul className="mt-3 divide-y divide-gray-100 rounded-md border border-gray-200 bg-white" data-id="element-10280" data-mp="1h8lgspjdk6t1">
              {addCandidates.length === 0 ? <li className="px-3 py-3 text-sm text-gray-500" data-id="element-10281" data-mp="1c8pj1819tblx">
                  No patients match "{addSearch}".
                </li> : addCandidates.map(p => {
          const name = `${p.firstName} ${p.lastName}`;
          const inQueue = queuedPatientIds.has(p.id);
          return <li key={p.id} className="flex items-center justify-between gap-3 px-3 py-2.5" data-id="element-10282" data-mp="1v2codsdjpgm4">
                      <div className="min-w-0" data-id="element-10283" data-mp="2jmg3jrlwcejr">
                        <p className="truncate text-sm font-medium text-gray-900" data-id="element-10284" data-mp="2jl505swd58t6">
                          {name}
                        </p>
                        <p className="text-xs text-gray-500" data-id="element-10285" data-mp="29ngu46tb4evp">
                          {p.bmcFileNumber} · {p.age} yrs · {p.sex}
                        </p>
                      </div>
                      <Button size="sm" variant="outline" disabled={inQueue} onClick={() => handleAdd(p.id, name)} data-id="element-10286" data-mp="20n24iv9b8dme">
                        {inQueue ? 'Already waiting' : 'Add'}
                      </Button>
                    </li>;
        })}
            </ul>}
        </div>}

      <div className="relative mb-4" data-id="element-10287" data-mp="1gxrqdlp7q0qe">
        <label htmlFor="queue-search" className="sr-only" data-id="element-10288" data-mp="1h0w2d58x60rm">
          Search queue
        </label>
        <SearchIcon className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" data-id="element-10289" data-mp="3jnjjhd477yh7" />
        <input id="queue-search" type="search" value={search} onChange={e => setSearch(e.target.value)} placeholder="Search queue by name or BMC number" className="block w-full rounded-md border border-gray-300 py-2 pl-9 pr-3 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500" data-id="element-10290" data-mp="39vx20z19p360" />
      </div>

      {visible.length === 0 ? <div className="py-12 text-center text-gray-500" data-id="element-10291" data-mp="3sln4m8b9ut8h">
          <UsersIcon className="mx-auto mb-2 h-10 w-10 text-gray-300" data-id="element-10292" data-mp="3so9cya5zlcrr" />
          <p className="text-sm" data-id="element-10293" data-mp="3okjpjo11t18b">
            {queue.length === 0 ? 'No patients are waiting right now.' : `No waiting patients match "${search}".`}
          </p>
        </div> : <ol className="divide-y divide-gray-100" data-id="element-10294" data-mp="2d7r1oobdowxj">
          {visible.map(({
        encounter,
        patient
      }) => {
        const position = queue.findIndex(row => row.encounter.id === encounter.id) + 1;
        const minutes = Math.max(0, Math.floor((now - new Date(encounter.startedAt).getTime()) / 60000));
        const stage = stageLabel[encounter.stage];
        return <li key={encounter.id} className="flex flex-col gap-2 py-3 sm:flex-row sm:items-center sm:gap-4" data-id="element-10295" data-mp="1bewjtl9e73db">
                <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-gray-100 text-sm font-bold text-gray-700" data-id="element-10296" data-mp="1gf12nhxrwa6p">
                  {position}
                </span>
                <div className="min-w-0 flex-1" data-id="element-10297" data-mp="1pbs4kaz0aj97">
                  <p className="truncate font-medium text-gray-900" data-id="element-10298" data-mp="3m6y3x8chphnu">
                    {patient.firstName} {patient.lastName}
                  </p>
                  <p className="text-xs text-gray-500" data-id="element-10299" data-mp="3r7c2lzs7bf97">
                    {patient.bmcFileNumber} · {patient.age} yrs · {patient.sex}
                  </p>
                </div>
                <span className={`inline-flex w-fit rounded-full px-2.5 py-1 text-xs font-semibold ${stage.className}`} data-id="element-10300" data-mp="1svafdebquxj6">
                  {stage.label}
                </span>
                <span className="inline-flex items-center gap-1 text-xs text-gray-500 sm:w-28 sm:justify-end" data-id="element-10301" data-mp="21q70n58qo0ne">
                  <ClockIcon className="h-3.5 w-3.5" data-id="element-10302" data-mp="1e0cokpw8ug47" />
                  {minutes < 60 ? `${minutes} min` : `${Math.floor(minutes / 60)} h ${minutes % 60} min`}{' '}
                  waiting
                </span>
              </li>;
      })}
        </ol>}
    </Card>;
}